import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { clampOffsetMinutes, offsetToSlotIndex, snapOffsetMinutes } from '../lib/time';

/** How often the shared "now" advances. Keeps a slider parked on Now honest. */
const CLOCK_TICK_MS = 30_000;

interface TimeContextType {
  /** Minutes into the future the user is previewing, 0–720, snapped to 30. */
  offsetMinutes: number;
  /** Index into each bar's `timeline` for the current offset. */
  slotIndex: number;
  /** True when the map is showing live prices rather than a future preview. */
  isNow: boolean;
  /**
   * Wall-clock instant the offset is measured from, shared by every consumer
   * so the slider and the bottom sheet can never disagree by a minute.
   */
  viewedAt: Date;
  setOffsetMinutes: (minutes: number) => void;
  resetToNow: () => void;
}

const TimeContext = createContext<TimeContextType | null>(null);

export function TimeProvider({ children }: { children: React.ReactNode }) {
  const [offsetMinutes, setOffsetMinutesState] = useState(0);
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), CLOCK_TICK_MS);
    return () => clearInterval(id);
  }, []);

  const setOffsetMinutes = useCallback((minutes: number) => {
    // Snap on write so every consumer sees a value that maps cleanly onto a
    // timeline slot, no matter how the caller derived it (drag, key, URL).
    setOffsetMinutesState((previous) => {
      const next = snapOffsetMinutes(minutes);
      return next === previous ? previous : next;
    });
  }, []);

  const resetToNow = useCallback(() => setOffsetMinutesState(0), []);

  const value = useMemo<TimeContextType>(
    () => ({
      offsetMinutes: clampOffsetMinutes(offsetMinutes),
      slotIndex: offsetToSlotIndex(offsetMinutes),
      isNow: offsetMinutes === 0,
      viewedAt: new Date(nowMs + clampOffsetMinutes(offsetMinutes) * 60_000),
      setOffsetMinutes,
      resetToNow,
    }),
    [offsetMinutes, nowMs, setOffsetMinutes, resetToNow]
  );

  return <TimeContext.Provider value={value}>{children}</TimeContext.Provider>;
}

export function useTime() {
  const context = useContext(TimeContext);
  if (!context) {
    throw new Error('useTime must be used within a TimeProvider');
  }
  return context;
}
